import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import i18n from '../../i18n/i18n';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Helper function to get current language
const getCurrentLanguage = () => {
  return i18n.language || 'ar';
};

// Helper function to get auth token
const getAuthToken = () => {
  const storedUser = sessionStorage.getItem("adminkhadamat");
  if (!storedUser) return null;
  try {
    const parsed = JSON.parse(storedUser);
    // البحث عن التوكن في أماكن مختلفة
    if (parsed.data?.token?.access_token) {
      const token = String(parsed.data.token.access_token);
      return token;
    }
    if (parsed.data?.access_token) return parsed.data.access_token;
    if (parsed.data?.token) return parsed.data.token;
    if (parsed.token) return parsed.token;
    if (parsed.result?.token) return parsed.result.token;
    if (parsed.user?.token) return parsed.user.token;
    return null;
  } catch {
    return null;
  }
};

// Fetch all permissions
export const fetchPermissions = createAsyncThunk(
  'permissions/fetchPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const currentLang = getCurrentLanguage();
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.get(`${BASE_URL}/admin/permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': currentLang,
          'lang': currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'فشل في جلب الصلاحيات');
    }
  }
);

// Fetch roles
export const fetchRoles = createAsyncThunk(
  'permissions/fetchRoles',
  async (_, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const currentLang = getCurrentLanguage();
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.get(`${BASE_URL}/admin/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': currentLang,
          'lang': currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'فشل في جلب الأدوار');
    }
  }
);

// Fetch role details
export const fetchRoleDetails = createAsyncThunk(
  'permissions/fetchRoleDetails',
  async (roleId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const currentLang = getCurrentLanguage();
      
  
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const url = `${BASE_URL}/admin/roles/${roleId}`;
    
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': currentLang,
          'lang': currentLang,
        },
      });
      return response.data;
    } catch (error) {
     
      return rejectWithValue(error.response?.data?.message || 'فشل في جلب تفاصيل الدور');
    }
  }
);

// Create role
export const createRole = createAsyncThunk(
  'permissions/createRole',
  async (roleData, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const currentLang = getCurrentLanguage();
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.post(`${BASE_URL}/admin/roles`, roleData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': currentLang,
          'lang': currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'فشل في إنشاء الدور');
    }
  }
);

// Update role
export const updateRole = createAsyncThunk(
  'permissions/updateRole',
  async ({ roleId, roleData }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const currentLang = getCurrentLanguage();
      

      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const url = `${BASE_URL}/admin/roles/${roleId}`;
 
      
      const response = await axios.put(url, roleData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': currentLang,
          'lang': currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'فشل في تحديث الدور');
    }
  }
);

// Assign permissions to role
export const assignPermissionsToRole = createAsyncThunk(
  'permissions/assignPermissionsToRole',
  async ({ roleId, permissions }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const currentLang = getCurrentLanguage();
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // تحويل الصلاحيات إلى الشكل المطلوب
      const permissionsData = permissions.map((permissionId, index) => ({
        [`permissions[${index}]`]: permissionId
      })).reduce((acc, curr) => ({ ...acc, ...curr }), {});
      
      const response = await axios.post(`${BASE_URL}/admin/roles/${roleId}/permissions`, permissionsData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': currentLang,
          'lang': currentLang,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'فشل في ربط الصلاحيات بالدور');
    }
  }
);

// Delete role
export const deleteRole = createAsyncThunk(
  'permissions/deleteRole',
  async (roleId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const currentLang = getCurrentLanguage();
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.delete(`${BASE_URL}/admin/roles/${roleId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': currentLang,
          'lang': currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'فشل في حذف الدور');
    }
  }
);

// Assign role to admin
export const assignRoleToAdmin = createAsyncThunk(
  'permissions/assignRoleToAdmin',
  async ({ adminId, roleId }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const currentLang = getCurrentLanguage();
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.post(`${BASE_URL}/admin/assign-role`, {
        admin_id: adminId,
        role_id: roleId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': currentLang,
          'lang': currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'فشل في تعيين الدور');
    }
  }
);

const initialState = {
  permissions: [],
  roles: [],
  roleDetails: null,
  userRoles: [],
  userPermissions: [],
  loading: false,
  error: null,
  rolesLoading: false,
  rolesError: null,
  roleDetailsLoading: false,
  roleDetailsError: null
};

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.rolesError = null;
    },
    setUserRoles: (state, action) => {
      state.userRoles = action.payload.roles || [];
      // Extract permissions from roles
      const allPermissions = [];
      action.payload.roles?.forEach(role => {
        if (role.permissions) {
          // إذا كانت الصلاحيات array من strings
          if (Array.isArray(role.permissions)) {
            allPermissions.push(...role.permissions);
          }
          // إذا كانت الصلاحيات array من objects
          else if (role.permissions && typeof role.permissions === 'object') {
            Object.values(role.permissions).forEach(permission => {
              if (typeof permission === 'string') {
                allPermissions.push(permission);
              } else if (permission && typeof permission === 'object' && permission.name) {
                allPermissions.push(permission.name);
              }
            });
          }
        }
      });
      state.userPermissions = [...new Set(allPermissions)]; // Remove duplicates
    },
    clearUserData: (state) => {
      state.userRoles = [];
      state.userPermissions = [];
    }
  },
  extraReducers: (builder) => {
    // Fetch permissions
    builder
      .addCase(fetchPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.permissions = action.payload.data || [];
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch roles
    builder
      .addCase(fetchRoles.pending, (state) => {
        state.rolesLoading = true;
        state.rolesError = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.roles = action.payload.data || [];
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesError = action.payload;
      });

    // Fetch role details
    builder
      .addCase(fetchRoleDetails.pending, (state) => {
        state.roleDetailsLoading = true;
        state.roleDetailsError = null;
      })
      .addCase(fetchRoleDetails.fulfilled, (state, action) => {
        state.roleDetailsLoading = false;
        state.roleDetails = action.payload.data;
      })
      .addCase(fetchRoleDetails.rejected, (state, action) => {
        state.roleDetailsLoading = false;
        state.roleDetailsError = action.payload;
      });

    // Create role
    builder
      .addCase(createRole.pending, (state) => {
        state.rolesLoading = true;
        state.rolesError = null;
      })
      .addCase(createRole.fulfilled, (state, action) => {
        state.rolesLoading = false;
        if (action.payload.data) {
          state.roles.push(action.payload.data);
        }
      })
      .addCase(createRole.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesError = action.payload;
      });

    // Update role
    builder
      .addCase(updateRole.pending, (state) => {
        state.rolesLoading = true;
        state.rolesError = null;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.rolesLoading = false;
        if (action.payload.data) {
          const index = state.roles.findIndex(role => role.id === action.payload.data.id);
          if (index !== -1) {
            state.roles[index] = action.payload.data;
          }
        }
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesError = action.payload;
      });

    // Assign permissions to role
    builder
      .addCase(assignPermissionsToRole.pending, (state) => {
        state.rolesLoading = true;
        state.rolesError = null;
      })
      .addCase(assignPermissionsToRole.fulfilled, (state, action) => {
        state.rolesLoading = false;
        // يمكن إضافة منطق إضافي هنا إذا لزم الأمر
      })
      .addCase(assignPermissionsToRole.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesError = action.payload;
      });

    // Delete role
    builder
      .addCase(deleteRole.pending, (state) => {
        state.rolesLoading = true;
        state.rolesError = null;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.roles = state.roles.filter(role => role.id !== action.meta.arg);
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesError = action.payload;
      });

    // Assign role to admin
    builder
      .addCase(assignRoleToAdmin.pending, (state) => {
        state.rolesLoading = true;
        state.rolesError = null;
      })
      .addCase(assignRoleToAdmin.fulfilled, (state, action) => {
        state.rolesLoading = false;
      })
      .addCase(assignRoleToAdmin.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesError = action.payload;
      });
  }
});

export const { clearError, setUserRoles, clearUserData } = permissionsSlice.actions;
export default permissionsSlice.reducer;
